import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import { Search, X } from 'lucide-react';
import Button from '../Button';
import Avatar from '../Avatar';
import UserName from '../UserName';
import supabase from '../../lib/supabase';

const schema = z.object({
  recipient: z.string().min(1, 'Please select a recipient'),
  message: z.string().min(1, 'Message cannot be empty'),
});

type FormData = z.infer<typeof schema>;

interface NewMessageModalProps {
  onClose: () => void;
  onSuccess: (conversationId: string) => void;
}

export default function NewMessageModal({ onClose, onSuccess }: NewMessageModalProps) {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  React.useEffect(() => {
    if (selectedUser) {
      setValue('recipient', selectedUser.id);
    } else {
      setValue('recipient', '');
    }
  }, [selectedUser, setValue]);

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, display_name, verified, avatar_url')
        .or(`full_name.ilike.%${query}%,display_name.ilike.%${query}%`)
        .neq('id', user?.id)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectUser = (selectedUser: any) => {
    setSelectedUser(selectedUser);
    setSearchQuery('');
    setSearchResults([]);
  };

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Get or create conversation using the function
      const { data: result, error: funcError } = await supabase
        .rpc('get_or_create_conversation', {
          user1: user.id < data.recipient ? user.id : data.recipient,
          user2: user.id < data.recipient ? data.recipient : user.id
        });

      if (funcError) throw funcError;

      const conversationId = result;

      // Add message
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: data.message
        });

      if (messageError) throw messageError;

      // Update conversation last message
      await supabase
        .from('conversations')
        .update({
          last_message: data.message,
          last_message_at: new Date().toISOString()
        })
        .eq('id', conversationId);

      onSuccess(conversationId);
    } catch (err: any) {
      console.error('Failed to create conversation:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-20">
      <div className="bg-neutral rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-secondary border-b border-secondary p-4 flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold font-dillan text-primary">New Message</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-light rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-6">
          {error && (
            <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
              {error}
            </div>
          )}

          <div>
            {selectedUser ? (
              <div className="flex items-center justify-between p-2 bg-secondary rounded-full text-primary">
                <div className="flex items-center space-x-2">
                  <Avatar
                    src={selectedUser.avatar_url}
                    alt={selectedUser.display_name || selectedUser.full_name}
                    size="sm"
                  />
                  <UserName
                    name={selectedUser.display_name || selectedUser.full_name}
                    verified={selectedUser.verified}
                    size="sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  className="p-1 text-neutral hover:text-secondary-dark rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleSearch(e.target.value);
                  }}
                  className="w-full h-12 px-4 pl-10 rounded-full border border-secondary-light bg-secondary-light text-primary placeholder:text-primary focus:border-secondary-light focus:ring-0 focus:outline-none"
                  placeholder="Search user"
                />
              </div>
            )}

            {/* Hidden input for form validation */}
            <input type="hidden" {...register('recipient')} />
            {errors.recipient && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.recipient.message}</p>
            )}

            {/* Search Results */}
            {searchQuery && !selectedUser && (
              <div className="mt-2 bg-secondary-light rounded-xl shadow-elevation-2 overflow-hidden text-primary">
                {isSearching ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-accent mx-auto"></div>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="divide-y divide-neutral divide-opacity-10">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleSelectUser(user)}
                        className="w-full p-3 flex items-center space-x-3 hover:bg-secondary transition-colors"
                      >
                        <Avatar
                          src={user.avatar_url}
                          alt={user.display_name || user.full_name}
                          size="sm"
                        />
                        <UserName
                          name={user.display_name || user.full_name}
                          verified={user.verified}
                          size="sm"
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-neutral">
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <textarea
              {...register('message')}
              className="w-full h-20 p-2 pl-4 rounded-full border border-secondary-light bg-secondary-light text-primary placeholder:text-primary focus:border-secondary-light focus:ring-0 focus:outline-none"
              placeholder="Type your message..."
            />
            {errors.message && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.message.message}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 mt-4">
            <Button
              type="button"
              variant="primary"
              className="text-surface-light"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="text-surface-light"
              disabled={isSubmitting || !selectedUser}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}